import React, { useState, useEffect } from "react";
import {
    PieChart,
    Pie,
    Cell,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";
import { useOutletContext } from "react-router-dom";
import { getData } from "../../../webService/webservice.js";
import "../../../styles/Auth/report.css";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useStore } from "../../../Context/context.jsx";
import Footer from "../Footer/Footer-page.jsx";
const COLORS = ["#A25B65", "#E74C3C", "#9B59B6", "#F1C40F", "#3498DB"];

const GraphReport = () => {
    const { startDate, endDate } = useOutletContext();
    const [activeMenu, setActiveMenu] = useState("Customer type chart");
    const [chartData, setChartData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const { selectedStoreId } = useStore();

    const fetchReport = async (type) => {
        try {


            let endpoint = "";
            let params = { storeId: selectedStoreId };

            switch (type) {
                case "Customer type chart":
                    endpoint = "reports/reports/customertype";
                    params.startDate = startDate;
                    params.endDate = endDate;
                    break;

                case "Booking per Day":
                    endpoint = "reports/reports/bookingperday";
                    params.date = startDate;
                    break;

                case "Booking per Month":
                    endpoint = "reports/reports/bookingpermonth";
                    params.month = selectedMonth;
                    params.year = selectedYear;
                    break;

                case "Booking per Year":
                    endpoint = "reports/reports/bookingperyear";
                    params.year = selectedYear;
                    break;

                default:
                    return;
            }

            const response = await getData(endpoint, params, sessionToken);

            // ✅ Improved Check: supports both 'success' flag and 'status' code
            if (response?.success === false || (response?.status && response?.status !== 200)) {
                toast.error(response?.message || "Failed to fetch Report");
                return;
            }

            let cleanedData = response?.data || [];

            if (type === "Customer type chart") {
                cleanedData = cleanedData.map(item => ({
                    name: item?.name?.trim() ? item.name : "Unknown",
                    value: item?.value || 0
                }));
            } else {
                cleanedData = cleanedData.map(item => ({
                    name: item?.name || "Unknown",
                    value: item?.value || 0
                }));
            }

            setChartData(cleanedData);

            // ✅ Optional success toast
            toast.success(response?.message || "Report fetched successfully");

        } catch (err) {
            console.error("❌ Error fetching report:", err);
            toast.error("Something went wrong while fetching report");
        }
    };


    useEffect(() => {
        fetchReport(activeMenu);
    }, [activeMenu, startDate, endDate, selectedMonth, selectedYear, selectedStoreId]);



    return (
        <div className="charts-container">
            <div className="charts-sidebar">
                <h3>Charts</h3>
                <ul>
                    {["Customer type chart", "Booking per Day", "Booking per Month", "Booking per Year"].map((item) => (
                        <li
                            key={item}
                            className={activeMenu === item ? "active" : ""}
                            onClick={() => setActiveMenu(item)}
                        >
                            {item}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="charts-content">
                <h3>{activeMenu}</h3>

                {/* Filters */}
                {/* Booking per Day now uses startDate from context */}


                {activeMenu === "Booking per Month" && (
                    <div className="filters">
                        <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>

                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            style={{ marginLeft: "10px" }}
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                )}

                {activeMenu === "Booking per Year" && (
                    <div className="filters">
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={{ height: '300px' }}>
                    {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            {activeMenu === "Customer type chart" ? (
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={3}
                                        dataKey="value"
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Legend verticalAlign="bottom" height={36} />
                                </PieChart>
                            ) : (
                                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                    <XAxis
                                        dataKey="name"
                                        label={{
                                            value:
                                                activeMenu === "Booking per Day"
                                                    ? "Hour"
                                                    : activeMenu === "Booking per Month"
                                                        ? "Day"
                                                        : "Month",
                                            position: "insideBottomRight",
                                            offset: -10,
                                        }}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        label={{ value: "Bookings", angle: -90, position: "insideLeft" }}
                                    />
                                    <Tooltip formatter={(value) => [`${value} Booking(s)`]} />
                                    <Bar dataKey="value" barSize={40} radius={[5, 5, 0, 0]}>
                                        {chartData.map((entry, index) => (
                                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            )}
                        </ResponsiveContainer>
                    ) : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#64748b',
                            gap: '12px'
                        }}>
                            <Icon icon="mdi:chart-scatter-plot-off" width="48" style={{ opacity: 0.4 }} />
                            <span style={{ fontWeight: 600 }}>No analytics found for {activeMenu}</span>
                            <p style={{ fontSize: '13px', opacity: 0.7, margin: 0 }}>There is no data to visualize for the selected criteria.</p>
                        </div>
                    )}
                </div>

            </div>
            <Footer />
        </div>

    );
};

export default GraphReport;
